/**
 * GameAgent — handles "create/add/schedule a game" requests by parsing
 * any details the coach provided (date, opponent, time) and then opening
 * the inline `CoachSteveCreateGameForm` to confirm the rest.
 *
 * Phase A keeps the UX identical to the previous inline implementation;
 * later phases will replace `match` with the LLM router.
 */

import { isCreateGameIntent } from './intents';
import { parseGameDetailsFromMessage } from './parsers';
import type { AgentRunContext, AgentRunResult, CoachSteveAgent } from './types';

/**
 * Pre-parse any mentioned game details and surface the inline form.
 * Same behavior as the `isCreateGameIntent(text)` branch in CoachSteve.tsx.
 */
export function startCreateGameFlow(text: string, ctx: AgentRunContext): void {
  ctx.addMessage('user', text.trim());
  const parsed = parseGameDetailsFromMessage(text);
  const hasDetails = parsed.opponent_name || parsed.date || parsed.game_time;
  ctx.addMessage(
    'assistant',
    hasDetails
      ? "Got it — I've filled in what you said. Confirm or edit below, then create the game."
      : "Sure — fill in the details below to add a game to the schedule."
  );
  ctx.setCreateGameInitialValues(Object.keys(parsed).length ? parsed : null);
  ctx.setPendingAction('create_game');
}

export const gameAgent: CoachSteveAgent = {
  name: 'game',
  description:
    'Adds a game to the team schedule. Parses date/opponent/time from the message, then opens the create-game form for confirmation.',
  applicableSports: 'all',
  match: (text) => isCreateGameIntent(text),
  async handle(text, ctx): Promise<AgentRunResult> {
    startCreateGameFlow(text, ctx);
    return { kind: 'handled' };
  },
};
